import { Box, List, Tooltip, Typography } from "@mui/material";
import { SatelliteIcon } from "common/icons";
import { useTranslation } from "common/locales";
import {
  ArableField,
  Field,
  FertilizerEvent,
  FieldPreparationEvent,
  ManualDMInputResponse,
  ManualDMInputResponseItem,
  ReseedEvent,
  useFertilizerEvents,
  useFieldPreparationEvents,
  useReadingsForField,
  useReseedEvents,
  useYieldEvents,
  YieldEvent,
} from "model";
import { useMemo } from "react";
import FieldEventListItem from "./FieldEventListItem";
import IngestedFieldEventListItem from "./IngestedFieldEventListItem";

const BELLFLOWER_COLOR = "#D3C9E7";

// Field-scoped view of the farm-wide ingested-item event types (fertiliser,
// seeding, harvest, field prep). These have no field FK on the backend — only
// a fieldGuids array in their payload — so there's no per-field endpoint;
// like RecordsPage.tsx, we fetch farm-wide and filter client-side.
export type FieldIngestedEvent =
  | { type: "seeding"; date: string; guid: string; event: ReseedEvent }
  | { type: "fertiliser"; date: string; guid: string; event: FertilizerEvent }
  | { type: "harvest"; date: string; guid: string; event: YieldEvent }
  | {
      type: "fieldPrep";
      date: string;
      guid: string;
      event: FieldPreparationEvent;
    };

export default function FieldEventList({
  field,
}: {
  field: Field | ArableField;
}) {
  const { t } = useTranslation();

  // Use React Query hooks that will trigger the data fetch
  const { data: allReadings = [] } = useReadingsForField(field.guid);
  // Farm-wide fetches, filtered by field client-side below — these event
  // types have no field FK on the backend, only a fieldGuids array in their
  // payload, so there's no per-field endpoint (same approach RecordsPage.tsx
  // uses for the farm-wide Records list).
  const { data: reseedEvents = [] } = useReseedEvents(field.farm);
  const { data: fertilizerEvents = [] } = useFertilizerEvents(field.farm);
  const { data: yieldEvents = [] } = useYieldEvents(field.farm);
  const { data: fieldPreparationEvents = [] } = useFieldPreparationEvents(
    field.farm
  );

  // Filter readings to only show non-rejected events for this field
  const manualDMForField = useMemo(() => {
    return allReadings.filter(
      (reading) =>
        reading.manualDryMatterInput.field === field.guid &&
        reading.manualDryMatterInput.verificationStatus !== "Rejected"
    );
  }, [allReadings, field.guid]);

  const ingestedEventsForField = useMemo(() => {
    const items: FieldIngestedEvent[] = [];
    for (const ev of reseedEvents) {
      if (ev.fieldGuids?.includes(field.guid)) {
        items.push({
          type: "seeding",
          date: ev.reseedDate,
          guid: ev.guid,
          event: ev,
        });
      }
    }
    for (const ev of fertilizerEvents) {
      if (ev.fieldGuids?.includes(field.guid)) {
        items.push({
          type: "fertiliser",
          date: ev.fertilizerApplicationDate,
          guid: ev.guid,
          event: ev,
        });
      }
    }
    for (const ev of yieldEvents) {
      const fieldGuids =
        ev.yieldDetails && "fieldGuids" in ev.yieldDetails
          ? ev.yieldDetails.fieldGuids
          : [];
      if (fieldGuids.includes(field.guid)) {
        items.push({
          type: "harvest",
          date: ev.yieldEventDate,
          guid: ev.guid,
          event: ev,
        });
      }
    }
    for (const ev of fieldPreparationEvents) {
      if (ev.fieldGuids?.includes(field.guid)) {
        items.push({
          type: "fieldPrep",
          date: ev.fieldPreparationEventDate,
          guid: ev.guid,
          event: ev,
        });
      }
    }
    return items;
  }, [
    reseedEvents,
    fertilizerEvents,
    yieldEvents,
    fieldPreparationEvents,
    field.guid,
  ]);

  // see https://stackoverflow.com/questions/51528780/typescript-check-typeof-against-custom-type
  const isManualDMInputResponse = (x: unknown): x is ManualDMInputResponse =>
    typeof x === "object" && x !== null && "manualDryMatterInput" in x;
  const isFieldIngestedEvent = (x: unknown): x is FieldIngestedEvent =>
    typeof x === "object" && x !== null && "event" in x;

  type FieldTimelineEvent = ManualDMInputResponse | FieldIngestedEvent;

  const getDate = (event: FieldTimelineEvent): Date => {
    if (isManualDMInputResponse(event)) {
      return new Date(getLatestTime(event.manualDryMatterInput));
    }
    if (isFieldIngestedEvent(event)) {
      return new Date(event.date);
    }
    return new Date(1);
  };

  let allEvents: Array<FieldTimelineEvent> = [];

  allEvents = allEvents.concat(manualDMForField).concat(ingestedEventsForField);

  // This will now sort on every render and we did not find out how to fix it, see https://github.com/robofarmio/web-app-frontend/pull/775#discussion_r1502445622
  // we might go back to 2021 methods like "onMount" etc to be able to compare if we find this is too slow
  const sortedEventList = allEvents.sort(
    (a: FieldTimelineEvent, b: FieldTimelineEvent) => {
      const dateA: Date = getDate(a);
      const dateB: Date = getDate(b);
      return dateB.valueOf() - dateA.valueOf();
    }
  );

  return (
    <Box sx={{ mb: 2 }}>
      {/* Section Header */}
      <Typography
        variant="body2"
        sx={{
          color: "text.secondary",
          fontWeight: 600,
          mb: 1,
        }}
      >
        {t("fieldevents.list.title")}
      </Typography>

      {/* Events List Container */}
      <Box
        sx={{
          backgroundColor: "white",
          borderRadius: 2,
          border: `1px solid ${BELLFLOWER_COLOR}`,
          overflow: "hidden",
        }}
      >
        <List sx={{ py: 0 }}>
          {sortedEventList.map((item: FieldTimelineEvent) => {
            if (isManualDMInputResponse(item)) {
              // ManualDMInputResponse items only ever come from readings
              // (useReadingsForField above), which don't exist for arable
              // fields — so this branch never actually receives an
              // ArableField at runtime.
              return (
                <FieldEventListItem
                  key={item.guid}
                  dm={item}
                  field={field as Field}
                />
              );
            }
            return <IngestedFieldEventListItem key={item.guid} item={item} />;
          })}
        </List>
      </Box>
    </Box>
  );
}

export function AutoDetectedIcon() {
  const { t } = useTranslation();
  return (
    <Tooltip title={t("fieldevent.autodetected.tooltip")}>
      <div>
        <SatelliteIcon />
      </div>
    </Tooltip>
  );
}

function getLatestTime(response: ManualDMInputResponseItem) {
  const { event: e } = response;
  if (e.dateTimeOut) {
    // covers move out and grazing/partial grazing events
    return Date.parse(e.dateTimeOut);
  }

  if (e.dateTimeIn) {
    // covers move in event
    return Date.parse(e.dateTimeIn);
  }

  if (e.dateTimeCut) {
    // silage event
    return Date.parse(e.dateTimeCut);
  }

  return Date.parse(response.dateTimeMeasurementStart); // reading
}
